import { Boxes, ChevronLeft, LogOut, UserRound } from 'lucide-react';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ForgeContextSwitcher } from '../../../forge/components/layout/ForgeContextSwitcher';
import { ThemeToggle } from '../../../forge/components/layout/ThemeToggle';
import { ForgeTheme } from '../../../forge/hooks/useForgeTheme';
import { useArchonAccess } from '../../context/ArchonAccessContext';
import { ARCHON_NAV_SECTIONS } from './archonNavigation';

interface ArchonSidebarProps {
  theme: ForgeTheme;
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  /** Takes the sidebar off the page entirely; the layout's reveal handle brings it back. */
  hideSidebar: () => void;
  onDragStart: (event: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
  isMobile?: boolean;
  onMobileNavigate?: () => void;
}

export const ArchonSidebar: React.FC<ArchonSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  hideSidebar,
  onDragStart,
  toggleTheme,
  currentPath,
  isMobile = false,
  onMobileNavigate,
}) => {
  const { logout } = useAuthContext();
  const { canManageData } = useArchonAccess();
  const navigate = useNavigate();
  const effectiveCollapsed = isCollapsed && !isMobile;
  const sidebarClasses = [
    'forge-sidebar',
    'archon-sidebar',
    effectiveCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
    isMobile ? 'is-mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside
      className={sidebarClasses}
      style={{ width: !isMobile && !effectiveCollapsed ? width : undefined }}
      aria-label="Archon navigation"
    >
      <div className="archon-sidebar-brand">
        <Boxes aria-hidden="true" />
        <span>Archon</span>
      </div>
      <nav className="sidebar-nav" aria-label="Archon navigation">
        {ARCHON_NAV_SECTIONS.map((section) => (
          <div key={section.title} className="sidebar-section">
            <div className="sidebar-section-label">{section.title}</div>
            {section.items.map((item) => {
              if (item.ownerOnly && !canManageData) return null;
              const Icon = item.icon;
              const isActive = currentPath === item.path || currentPath.startsWith(`${item.path}/`);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  title={effectiveCollapsed ? item.label : undefined}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={onMobileNavigate}
                >
                  <Icon aria-hidden="true" />
                  <span className="sidebar-item-label">{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-footer-utilities">
          <ForgeContextSwitcher variant={isMobile ? 'drawer' : 'sidebar'} />
          <ThemeToggle
            theme={theme}
            onToggle={toggleTheme}
            variant="sidebar"
            collapsed={effectiveCollapsed}
          />
          <button
            className="sidebar-footer-link"
            onClick={() => {
              onMobileNavigate?.();
              navigate('/forge/profile');
            }}
            type="button"
          >
            <UserRound aria-hidden="true" />
            <span className="sidebar-collapse-label">Profile</span>
          </button>
          <button
            className="sidebar-footer-link"
            onClick={() => {
              onMobileNavigate?.();
              logout();
              navigate('/stores');
            }}
            type="button"
          >
            <LogOut aria-hidden="true" />
            <span className="sidebar-collapse-label">Sign out</span>
          </button>
        </div>
        {!isMobile ? (
          <>
            <div className="sidebar-footer-divider" />
            <button
              className="sidebar-collapse-btn"
              onClick={hideSidebar}
              type="button"
              aria-label="Hide navigation"
              title="Hide navigation"
            >
              <ChevronLeft aria-hidden="true" />
              <span className="sidebar-collapse-label">Hide</span>
            </button>
          </>
        ) : null}
      </div>
      {!isMobile ? <div className="sidebar-drag-handle" onMouseDown={onDragStart} /> : null}
    </aside>
  );
};
